import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, downloadLedger } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, WarningCircle, Fire, PencilSimple, TrendUp, Warning, DownloadSimple, ShoppingCart, Truck, CurrencyInr, XCircle, Factory } from "@phosphor-icons/react";

function StatCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-zinc-300 p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
        <Icon size={20} weight="bold" className={accent || "text-zinc-400"} />
      </div>
      <p className="font-mono text-4xl font-bold text-zinc-900 mt-4 tabular">{value}</p>
      {sub && <p className="text-xs text-zinc-400 mt-1">{sub}</p>}
    </div>
  );
}

const fmt = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  const [paying, setPaying] = useState(false);

  const loadStats = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadStats();
  }, []);

  const openPay = (p) => {
    setPayRow(p);
    setPayAmount(String(p.pending));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPaying(false);
  };

  const submitPay = async () => {
    if (!payRow) return;
    const amt = Number(payAmount);
    if (!(amt > 0)) return toast.error("Enter a payment amount");
    setPaying(true);
    try {
      await api.post(`/purchase-orders/${payRow.id}/pay`, { amount: amt, date: payDate || undefined });
      toast.success(`Payment of ₹${amt.toFixed(2)} recorded for ${payRow.po_number}`);
      setPayRow(null);
      loadStats();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setPaying(false);
    }
  };

  if (!stats)
    return (
      <>
        <PageHeader title="Dashboard" subtitle="Overview" />
        <div className="p-8 text-sm text-zinc-500">Loading control panel…</div>
      </>
    );

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Raw Material Overview">
        <Button variant="outline" onClick={() => downloadLedger("xlsx").catch(() => toast.error("Export failed"))} data-testid="export-excel-button" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">
          <DownloadSimple size={16} weight="bold" className="mr-1" /> Excel
        </Button>
        <Button variant="outline" onClick={() => downloadLedger("pdf").catch(() => toast.error("Export failed"))} data-testid="export-pdf-button" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">
          <DownloadSimple size={16} weight="bold" className="mr-1" /> PDF
        </Button>
      </PageHeader>
      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Materials" value={stats.total_materials} sub={`${stats.healthy_count} healthy`} icon={Package} accent="text-zinc-800" />
          <StatCard label="Low Stock" value={stats.low_count} sub="At or below threshold" icon={Warning} accent="text-[#EAB308]" />
          <StatCard label="Out of Stock" value={stats.out_count} sub="Needs restocking" icon={WarningCircle} accent="text-[#DC2626]" />
          <StatCard label="Ovens Produced" value={stats.total_produced} sub={stats.pending_confirmation > 0 ? `${stats.pending_confirmation} pending confirmation` : "Confirmed finished ovens"} icon={Fire} accent="text-primary" />
        </div>

        {/* Finished ovens by model */}
        <div className="bg-white border border-zinc-300" data-testid="finished-ovens-by-model">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
            <Fire size={18} weight="fill" className="text-primary" />
            <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Finished Ovens by Model</h3>
            <span className="text-[11px] text-zinc-400 normal-case font-normal">(confirmed only)</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200">
            {Object.keys(stats.produced_by_model || { "12L": 0, "18L": 0, "25L": 0 }).map((model) => {
              const isOven = /^\d+(\.\d+)?\s*L$/i.test(model.trim());
              return (
              <div key={model} className="p-6" data-testid={`finished-oven-${model}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{isOven ? `${model} Oven` : model}</p>
                  {isOven && <span className="font-mono text-xs font-bold bg-zinc-900 text-white px-2 py-0.5">{model}</span>}
                </div>
                <p className="font-mono text-4xl font-bold text-zinc-900 mt-4 tabular" data-testid={`finished-oven-count-${model}`}>{stats.produced_by_model?.[model] ?? 0}</p>
                <p className="text-xs text-zinc-400 mt-1">units confirmed</p>
              </div>
            );})}
          </div>
        </div>

        {/* Pending supplier deliveries */}
        {(stats.pending_deliveries?.length ?? 0) > 0 && (
          <div className="bg-white border border-zinc-300" data-testid="pending-deliveries">
            <div className="flex items-center gap-2 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
              <Truck size={18} weight="bold" className="text-primary" />
              <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Pending Supplier Deliveries</h3>
              <span className="font-mono text-xs font-bold bg-zinc-900 text-white px-2 py-0.5">{stats.pending_deliveries.length}</span>
            </div>
            <div className="divide-y divide-zinc-200">
              {stats.pending_deliveries.map((d) => (
                <button
                  key={d.id}
                  onClick={() => navigate("/purchase-orders")}
                  data-testid={`pending-delivery-${d.id}`}
                  className="w-full text-left flex items-center justify-between px-6 py-3 hover:bg-zinc-50 transition-colors duration-100"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs font-bold text-primary">{d.po_number}</span>
                    <span className="text-sm text-zinc-900">{d.supplier_name}</span>
                    <span className="text-[11px] uppercase tracking-wide text-zinc-400">{d.status}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase text-white ${d.overdue ? "bg-[#DC2626]" : "bg-[#EA580C]"}`}>
                      {d.overdue && <Warning size={11} weight="bold" />}
                      {d.overdue ? "Overdue" : "Due"} {d.expected_delivery_date}
                    </span>
                    <span className="font-mono text-sm tabular">₹{(d.total ?? 0).toFixed(2)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Overdue supplier payments */}
        {(stats.pending_payments?.length ?? 0) > 0 && (
          <div className="bg-white border border-zinc-300" data-testid="pending-payments">
            <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-2">
                <CurrencyInr size={18} weight="bold" className="text-[#DC2626]" />
                <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Overdue Payments</h3>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-bold text-[#DC2626]">₹{(stats.payments_summary?.total_pending ?? 0).toFixed(2)} overdue</span>
                <Button variant="outline" size="sm" onClick={() => navigate("/payments")} data-testid="payments-see-all" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">Report</Button>
              </div>
            </div>
            <div className="divide-y divide-zinc-100">
              {stats.pending_payments.map((p, pi) => {
                const pct = p.payable > 0 ? Math.min(100, Math.round((p.paid / p.payable) * 100)) : 0;
                return (
                <div key={`${p.id}-${pi}`} data-testid={`pending-payment-${p.id}-${pi}`} className="px-6 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <button onClick={() => navigate("/purchase-orders")} className="flex flex-wrap items-center gap-3 text-left hover:opacity-80 transition-opacity duration-100" data-testid={`pending-payment-open-${p.id}-${pi}`}>
                      <span className="font-mono text-xs font-bold text-primary">{p.po_number}</span>
                      <span className="text-sm text-zinc-900">{p.supplier_name}</span>
                      {p.delivery_date && <span className="text-[11px] text-zinc-400">delivered {p.delivery_date}</span>}
                    </button>
                    <div className="flex items-center gap-3">
                      {p.due_date && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase text-white bg-[#DC2626]">
                          <Warning size={11} weight="bold" />Overdue {p.due_date}
                        </span>
                      )}
                      <span className="font-mono text-sm font-bold text-[#DC2626]">₹{p.pending.toFixed(2)}</span>
                      <Button size="sm" onClick={() => openPay(p)} data-testid={`dashboard-pay-${p.id}-${pi}`} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white text-[11px] uppercase font-bold h-7">Pay</Button>
                    </div>
                  </div>
                  {p.paid > 0 && (
                    <div className="mt-2" data-testid={`pending-payment-progress-${p.id}-${pi}`}>
                      <div className="flex justify-between text-[11px] mb-1">
                        <span className="text-[#16A34A] font-medium">₹{p.paid.toFixed(2)} of ₹{p.payable.toFixed(2)} paid</span>
                        <span className="font-mono text-zinc-400">{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-zinc-200 w-full">
                        <div className="h-full bg-[#16A34A]" style={{ width: `${pct}%`, transition: "width 300ms ease" }} />
                      </div>
                    </div>
                  )}
                </div>
              );})}
            </div>
          </div>
        )}


        {/* Rejections & scrap */}
        {(stats.rejections_summary?.count ?? 0) > 0 && (
          <div className="bg-white border border-zinc-300" data-testid="rejections-summary">
            <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-2">
                <XCircle size={18} weight="bold" className="text-[#DC2626]" />
                <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Rejections &amp; Scrap</h3>
                <span className="text-[11px] text-zinc-400 normal-case font-normal">({stats.rejections_summary.po_count} PO · {stats.rejections_summary.plan_count} scrap)</span>
              </div>
              <button
                onClick={() => navigate("/rejections")}
                data-testid="see-all-rejections"
                className="text-[11px] font-bold uppercase tracking-wide text-primary hover:text-[#C2410C] transition-colors duration-100"
              >
                See All ({stats.rejections_summary.count})
              </button>
            </div>
            <div className="divide-y divide-zinc-200">
              {stats.rejections_summary.recent.map((r, ri) => (
                <button
                  key={`${r.type}-${r.ref_id}-${ri}`}
                  onClick={() => navigate(r.type === "po" ? `/purchase-orders/${r.ref_id}/timeline` : r.type === "production" ? "/production" : "/rejections")}
                  data-testid={`rejection-row-${ri}`}
                  className="w-full text-left flex flex-wrap items-center justify-between gap-3 px-6 py-3 hover:bg-zinc-50 transition-colors duration-100"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {r.type === "po" ? <Truck size={15} weight="bold" className="text-primary shrink-0" /> : r.type === "production" ? <Fire size={15} weight="bold" className="text-[#EA580C] shrink-0" /> : <Factory size={15} weight="bold" className="text-[#0891B2] shrink-0" />}
                    <span className="font-mono text-xs font-bold text-primary">{r.ref_number}</span>
                    <span className="text-sm text-zinc-900 truncate">{r.material_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase text-white ${r.type === "po" ? "bg-[#EA580C]" : r.type === "production" ? "bg-[#B91C1C]" : "bg-[#DC2626]"}`}>
                      {r.type === "po" ? "Rejected" : r.type === "production" ? "On-line" : "Scrap"}
                    </span>
                    <span className="font-mono text-sm font-bold text-[#DC2626]">{r.qty} {r.unit}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Low stock alerts */}
          <div className="bg-white border border-zinc-300">
            <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-2">
                <Warning size={18} weight="bold" className="text-[#DC2626]" />
                <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Low Stock Alerts</h3>
              </div>
              {stats.low_stock_items.length > 5 && (
                <button
                  onClick={() => window.open("/low-stock", "_blank", "noopener")}
                  data-testid="see-all-low-stock"
                  className="text-[11px] font-bold uppercase tracking-wide text-primary hover:text-[#C2410C] transition-colors duration-100"
                >
                  See All ({stats.low_stock_items.length})
                </button>
              )}
            </div>
            {stats.low_stock_items.length === 0 ? (
              <p className="p-6 text-sm text-zinc-400">All materials are healthy. Nothing to restock.</p>
            ) : (
              <div className="divide-y divide-zinc-200">
                {stats.low_stock_items.slice(0, 5).map((m) => (
                  <div key={m.id} className="w-full flex items-center justify-between px-6 py-3 hover:bg-zinc-50 transition-colors duration-100" data-testid={`low-stock-${m.id}`}>
                    <button onClick={() => navigate(`/materials/${m.id}`)} className="text-left flex-1">
                      <p className="text-sm font-medium text-zinc-900">{m.name}</p>
                      <p className="text-xs text-zinc-500">Min {m.min_stock} {m.unit}</p>
                    </button>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase text-white ${m.status === "out" ? "bg-[#DC2626]" : "bg-[#EAB308]"}`}>
                          {m.status}
                        </span>
                        <p className="font-mono text-sm mt-1 tabular">{m.current_stock} {m.unit}</p>
                      </div>
                      <button
                        onClick={() => navigate("/purchase-orders", { state: { reorder: m } })}
                        data-testid={`reorder-${m.id}`}
                        title="Create purchase order"
                        className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white bg-primary hover:bg-[#C2410C] px-2 py-1.5 transition-colors duration-100"
                      >
                        <ShoppingCart size={13} weight="bold" /> Reorder
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent corrections */}
          <div className="bg-white border border-zinc-300">
            <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-2">
                <PencilSimple size={18} weight="bold" className="text-primary" />
                <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Recent Corrections</h3>
              </div>
              {stats.corrections_count > 5 && (
                <button
                  onClick={() => window.open("/corrections", "_blank", "noopener")}
                  data-testid="see-all-corrections"
                  className="text-[11px] font-bold uppercase tracking-wide text-primary hover:text-[#C2410C] transition-colors duration-100"
                >
                  See All ({stats.corrections_count})
                </button>
              )}
            </div>
            {stats.recent_corrections.length === 0 ? (
              <p className="p-6 text-sm text-zinc-400">No stock corrections yet.</p>
            ) : (
              <div className="divide-y divide-zinc-200">
                {stats.recent_corrections.slice(0, 5).map((t) => (
                  <div key={t.id} className="px-6 py-3" data-testid={`correction-${t.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-zinc-900">{t.material_name}</p>
                      <span className={`font-mono text-sm tabular ${t.change >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                        {t.change >= 0 ? "+" : ""}{t.change} {t.unit}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">{t.reason}</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">{t.by_name} · {fmt(t.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent production */}
        <div className="bg-white border border-zinc-300">
          <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
            <div className="flex items-center gap-2">
              <TrendUp size={18} weight="bold" className="text-zinc-800" />
              <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Recent Production</h3>
            </div>
            {stats.production_count > 5 && (
              <button
                onClick={() => window.open("/production-log", "_blank", "noopener")}
                data-testid="see-all-production"
                className="text-[11px] font-bold uppercase tracking-wide text-primary hover:text-[#C2410C] transition-colors duration-100"
              >
                See All ({stats.production_count})
              </button>
            )}
          </div>
          {stats.recent_production.length === 0 ? (
            <p className="p-6 text-sm text-zinc-400">No production runs recorded yet.</p>
          ) : (
            <div className="divide-y divide-zinc-200">
              {stats.recent_production.slice(0, 5).map((r) => (
                <div key={r.id} className="flex items-center justify-between px-6 py-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold bg-zinc-900 text-white px-2 py-1">{r.model}</span>
                    {r.order_no && <span className="font-mono text-xs font-bold text-primary">{r.order_no}</span>}
                    <span className="text-sm text-zinc-900">{r.made ?? r.quantity} / {r.quantity} made</span>
                  </div>
                  <p className="text-[11px] text-zinc-400">{r.by_name} · {fmt(r.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!payRow} onOpenChange={(o) => { if (!o) setPayRow(null); }}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="dashboard-pay-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Record Payment</DialogTitle></DialogHeader>
          {payRow && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-500">
                {payRow.po_number} · {payRow.supplier_name} · Overdue <span className="font-mono text-[#DC2626]">₹{payRow.pending.toFixed(2)}</span>
              </p>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-[#16A34A]">Amount Paid (₹)</Label>
                  <Input type="number" min="0" data-testid="dashboard-pay-amount" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-[#16A34A]" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-zinc-500">Payment Date</Label>
                  <Input type="date" data-testid="dashboard-pay-date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-400">This payment is recorded against {payRow.po_number} and will reflect on the purchase order. Once fully paid, it drops off this list and reminders stop.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayRow(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={submitPay} disabled={paying} data-testid="dashboard-pay-confirm" className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold uppercase tracking-wider">
              {paying ? "Saving…" : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
